import { uuidv7 } from "uuidv7";
import type {
  AnyEvent,
  BatchAppData,
  IdentifyEvent,
  PageEvent,
  ScreenEvent,
  TrackEvent,
  EventType,
} from "../events/types.ts"; // Explicit extension
import {
  BatchQueue,
  type SetTimeoutFn,
  type ClearTimeoutFn,
} from "../queue/batchQueue.ts";

export interface AnalyticsConfig<H = unknown> {
  writeKey: string;
  host?: string;
  /**
   * Callback to issue the network request.
   * decoupling fetch/axios/etc implementation.
   */
  issueRequest: (
    data: BatchAppData,
    config: { writeKey: string; host: string },
  ) => Promise<void>;

  /**
   * Optional UUID generator. Defaults to uuidv7.
   */
  uuid?: () => string;

  /**
   * Optional timeout functions for the batch queue.
   * If not provided, defaults to global setTimeout/clearTimeout (if available).
   */
  setTimeout?: SetTimeoutFn<H>;
  clearTimeout?: ClearTimeoutFn<H>;

  batchSize?: number;
  batchTimeout?: number;
  baseDelay?: number;
  retries?: number;
}

export class SegmentflowCore<H = unknown> {
  private readonly batchQueue: BatchQueue<AnyEvent, H>;
  private readonly uuid: () => string;
  public readonly writeKey: string;
  public readonly host: string;

  constructor(config: AnalyticsConfig<H>) {
    this.writeKey = config.writeKey;
    this.host = config.host ?? "https://api.segmentflow.ai";
    this.uuid = config.uuid ?? uuidv7;

    const setTimeoutFn =
      config.setTimeout ??
      (((fn: () => void, ms: number) =>
        setTimeout(fn, ms)) as unknown as SetTimeoutFn<H>);
    const clearTimeoutFn =
      config.clearTimeout ??
      (((h: any) => clearTimeout(h)) as unknown as ClearTimeoutFn<H>);

    this.batchQueue = new BatchQueue({
      batchSize: config.batchSize ?? 5,
      timeout: config.batchTimeout ?? 500,
      setTimeout: setTimeoutFn,
      clearTimeout: clearTimeoutFn,
      baseDelay: config.baseDelay,
      retries: config.retries,
      executeBatch: async (batch) => {
        const data: BatchAppData = { batch, writeKey: this.writeKey };
        await config.issueRequest(data, {
          writeKey: this.writeKey,
          host: this.host,
        });
      },
    });
  }

  public identify(
    params: Omit<IdentifyEvent, "type" | "messageId" | "timestamp">,
  ): void {
    const event: IdentifyEvent = {
      type: "identify" as EventType.Identify, // Explicit cast or use Enum if imported
      messageId: this.uuid(),
      timestamp: new Date().toISOString(),
      ...params,
    };
    this.batchQueue.submit(event);
  }

  public track(
    params: Omit<TrackEvent, "type" | "messageId" | "timestamp">,
  ): void {
    const event: TrackEvent = {
      type: "track" as EventType.Track,
      messageId: this.uuid(),
      timestamp: new Date().toISOString(),
      ...params,
    };
    this.batchQueue.submit(event);
  }

  public page(
    params: Omit<PageEvent, "type" | "messageId" | "timestamp">,
  ): void {
    const event: PageEvent = {
      type: "page" as EventType.Page,
      messageId: this.uuid(),
      timestamp: new Date().toISOString(),
      ...params,
    };
    this.batchQueue.submit(event);
  }

  public screen(
    params: Omit<ScreenEvent, "type" | "messageId" | "timestamp">,
  ): void {
    const event: ScreenEvent = {
      type: "screen" as EventType.Screen,
      messageId: this.uuid(),
      timestamp: new Date().toISOString(),
      ...params,
    };
    this.batchQueue.submit(event);
  }

  public async flush(): Promise<void> {
    await this.batchQueue.flush();
  }
}
